import React, { useRef, useState } from "react";
import { areRewardedAdsEnabled } from '../../utils/rewardedAds';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { prepareReferralTransfer, transferReferralCoins } from "../../services/referralService";
import { GAMIFICATION_ROUTES } from "../gamification/gamificationRoutes";
import fallbackAvatar from "../../assets/images/profile1.png";
import { REFERRAL_ROUTES } from "./referralRoutes";
import { ReferralSummarySkeleton, ReferralPodiumSkeleton } from "./ReferralShimmers";
import { showManualCopyAlert, showReferralAlert } from "./referralAlerts";
import "./Referralearn.css";

const formatCoins = (value) => new Intl.NumberFormat("en-IN").format(Number(value) || 0);

function PreviewPerformer({ performer, place }) {
  if (!performer) return null;
  return (
    <div className={`rperformer ${place}`}>
      <div className={`rprofile-wrapper ${place === "first" ? "winner" : ""}`}>
        <img src={performer.image || fallbackAvatar} alt={performer.name} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = fallbackAvatar; }} />
        {place === "first" && <span className="winner-icon"><i className="bi bi-trophy-fill" /></span>}
        <span className="rrank-badge">{performer.rank || (place === "first" ? 1 : place === "second" ? 2 : 3)}</span>
      </div>
      <h3>{performer.name}</h3>
      <strong>{formatCoins(performer.score)} Coins</strong>
    </div>
  );
}

export default function MyRewardTab({ summaryQuery, leaderboardQuery }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const submitLock = useRef(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [recipientCode, setRecipientCode] = useState("");
  const [coinsText, setCoinsText] = useState("");
  const summary = summaryQuery.data;
  const leaderboard = leaderboardQuery.data?.pages?.[0];
  const topThree = leaderboard?.topThree || [];
  const [first, second, third] = topThree;
  const transferConfig = summary?.transfer_config;
  const transferDisabled = transferConfig?.enabled != null && Number(transferConfig.enabled) === 0;
  const transferRows = [
    ["Transferable referral coins", summary?.transferable_referral_coins],
    ["Available earned coins", summary?.available_earned_coins],
    ["Minimum transfer", transferConfig?.min_transfer],
    ["Maximum transfer", transferConfig?.max_transfer],
    ["Transfer fee", transferConfig?.fee_percent == null ? null : `${transferConfig.fee_percent}%`],
    ["Free transfers limit", transferConfig?.free_transfers_limit],
  ].filter(([, value]) => value != null);

  const transferMutation = useMutation({
    mutationFn: ({ code, coins }) => transferReferralCoins(code, coins),
    retry: false,
    onSuccess: (message) => {
      showReferralAlert({ icon: "success", title: "Coins transferred", text: message || "Your coins were transferred successfully." });
      setRecipientCode("");
      setCoinsText("");
      setTransferOpen(false);
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["sidebarData"] });
    },
    onError: (error) => showReferralAlert({ icon: "error", title: "Transfer failed", text: error?.message || "Failed to transfer coins." }),
    onSettled: () => { submitLock.current = false; },
  });

  const submitTransfer = (event) => {
    event.preventDefault();
    if (submitLock.current || transferMutation.isPending || transferDisabled) return;
    let transfer;
    try {
      transfer = prepareReferralTransfer(recipientCode, coinsText);
    } catch (error) {
      showReferralAlert({ icon: "warning", title: "Check transfer details", text: error.message });
      return;
    }
    submitLock.current = true;
    transferMutation.mutate(transfer);
  };

  const copyCode = async () => {
    if (!summary?.referral_code) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(summary.referral_code);
    } catch {
      await showManualCopyAlert("Copy your referral code", summary.referral_code);
      return;
    }
    await showReferralAlert({ icon: "success", title: "Referral code copied", text: "Share it with your friends to invite them." });
  };

  return (
    <div className="my-rewards-content">
      <div className="rewards-left">
        {summaryQuery.isPending ? <ReferralSummarySkeleton /> : summaryQuery.isError && !summary ? (
          <div className="referral-status" role="alert">
            <p>{summaryQuery.error?.message || "Unable to load referral rewards."}</p>
            <button type="button" onClick={() => summaryQuery.refetch()}>Retry</button>
          </div>
        ) : <>
          {summaryQuery.isError && <div className="referral-status" role="alert"><p>{summaryQuery.error?.message || "Unable to refresh rewards."}</p><button type="button" onClick={() => summaryQuery.refetch()}>Retry</button></div>}
          <div className="rsummary-card">
            <div className="rsummary-item"><div className="rsummary-icon rcoin-icon"><i className="bi bi-wallet2" /></div><div><strong>{formatCoins(summary?.transferable_referral_coins)}</strong><span>Total Coins Earned</span></div></div>
            <div className="rsummary-divider" />
            <div className="rsummary-item referral-summary"><div className="rsummary-icon referral-icon"><i className="fa-solid fa-users" /></div><div><strong>{formatCoins(summary?.stats?.total_referrals)}</strong><span>Total Referrals</span></div></div>
            <div className={`transfer-wrapper ${transferOpen ? "transfer-wrapper-open" : ""}`}>
              <button type="button" className="transfer-select" aria-expanded={transferOpen} onClick={() => setTransferOpen((open) => !open)}><span>Transfer Your Coin</span><i className={`bi ${transferOpen ? "bi-chevron-up" : "bi-chevron-down"}`} /></button>
              {transferOpen && <form className="transfer-content" onSubmit={submitTransfer}>
                {transferDisabled && <p className="referral-inline-note">Transfers are currently unavailable.</p>}
                <div className="transfer-field"><label htmlFor="referral-recipient">Enter referral code of the receiving user</label><input id="referral-recipient" type="text" value={recipientCode} onChange={(event) => setRecipientCode(event.target.value)} placeholder="Referral code" autoComplete="off" disabled={transferDisabled || transferMutation.isPending} /></div>
                <div className="transfer-field"><label htmlFor="referral-coins">Enter transfer coins</label><input id="referral-coins" type="text" inputMode="numeric" value={coinsText} onChange={(event) => setCoinsText(event.target.value)} placeholder="Coins" disabled={transferDisabled || transferMutation.isPending} /></div>
                {transferRows.length > 0 && <><button type="button" className="transfer-info" aria-expanded={infoOpen} onClick={() => setInfoOpen((open) => !open)}><span>Transfer Info</span><i className={`bi ${infoOpen ? "bi-chevron-up" : "bi-chevron-down"}`} /></button>{infoOpen && <dl className="referral-transfer-info">{transferRows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}</>}
                <button type="submit" className="send-transfer-btn" disabled={transferDisabled || transferMutation.isPending}>{transferMutation.isPending ? "Sending..." : "Send"}</button>
              </form>}
            </div>
          </div>
          <div className="invite-card">
            <div className="invite-top"><div className="invite-icon"><i className="bi bi-coin" /></div><div className="invite-title"><h3>Invite now</h3><p>Earn up to {formatCoins(summary?.rewards?.referrer?.coin_value)} Coins</p></div></div>
            <div className="d-flex align-items-center justify-content-between">
              <label>Referral Code</label>
              {areRewardedAdsEnabled() && <button type="button" className="watch-btn" onClick={() => navigate(GAMIFICATION_ROUTES.coinStore)}>
                <i className="bi bi-play-circle-fill" /> Watch &amp; Earn
              </button>}
            </div>
            <div className="referral-input"><strong>{summary?.referral_code || "Unavailable"}</strong><button type="button" onClick={copyCode} disabled={!summary?.referral_code} aria-label="Copy referral code"><i className="bi bi-copy" /></button></div>
          </div>
        </>}
      </div>
      {leaderboardQuery.isPending ? <ReferralPodiumSkeleton /> : leaderboardQuery.isError ? (
        <div className="referral-status" role="alert"><p>{leaderboardQuery.error?.message || "Unable to load top performers."}</p><button type="button" onClick={() => leaderboardQuery.refetch()}>Retry</button></div>
      ) : topThree.length > 0 ? <div className="rleaderboard"><div className="rleaderboard-heading"><h2>Top Performers</h2><button type="button" onClick={() => navigate(REFERRAL_ROUTES.leaderboard)}>View Leaderboard <span>&gt;&gt;</span></button></div><div className="rperformers"><PreviewPerformer performer={second} place="second" /><PreviewPerformer performer={first} place="first" /><PreviewPerformer performer={third} place="third" /></div></div> : null}
    </div>
  );
}
